import { Reducer } from "redux";
import { ApiUser, ApiUserPermission, FirebaseUser, GatewayUser, HxAuthUser, User, UserAuthType, UserRole } from "avvir";

import { decodeAvvirGatewayToken, decodeAvvirStorageToken, decodeJwtToPayload } from "../firebase_services/firebase_tokens";
import { FIREBASE_SIGNED_IN_WITH_CUSTOM_TOKEN, FirebaseSignedInWithCustomTokenEvent } from "../../events/auth_events/firebase_signed_in_with_custom_token";
import { FIREBASE_USER_SIGNED_IN, FirebaseUserSignedInEvent } from "../../events/auth_events/firebase_user_signed_in";
import { GATEWAY_USER_SIGNED_IN, GatewayUserSignedInEvent } from "../../events/auth_events/gateway_user_signed_in";
import { GCP_BEARER_TOKEN_LOADED, GcpBearerTokenLoadedEvent } from "../../events/loaded/gcp_bearer_token_loaded";
import { USER_ACCOUNT_LOADED, UserAccountLoadedEvent } from "../../events/auth_events/user_account_loaded";
import { USER_ACCOUNT_UPDATED, UserAccountUpdatedEvent } from "../../events/auth_events/user_account_updated";
import { USER_SIGNED_OUT, UserSignedOutEvent } from "../../events/auth_events/user_signed_out";
import { USER_PERMISSIONS_LOADED, UserPermissionsLoadedEvent } from "../../events/auth_events/user_permissions_loaded";
import { HXAUTH_USER_SIGNED_IN, HxAuthUserSignedInEvent } from "../../events/auth_events/hxauth_user_signed_in";

export type UserAccount = {
  name?: string,
  email: string,
  userOrganization?: string
  role: UserRole,
  projectId?: string,
  userId?: number,
  expireTime?: number
}

export type SessionStore = {
  bimtrackAuthCode?: string
  userRole: string
  procoreAccessToken?: string
  account: UserAccount
  gcpBearerTokensByFirebaseOrganizationId?: {
    [firebaseId: string]: {
      organizationId: string
      accessToken: string
      expireTime: Date
    }
  },
  permissions: ApiUserPermission[]
}

export type UserSessionStoreEvent =
  | GcpBearerTokenLoadedEvent
  | FirebaseSignedInWithCustomTokenEvent
  | FirebaseUserSignedInEvent
  | GatewayUserSignedInEvent
  | UserSignedOutEvent
  | { type: "to_procore_authenticated_page" }
  | UserAccountUpdatedEvent
  | UserAccountLoadedEvent
  | UserPermissionsLoadedEvent
  | HxAuthUserSignedInEvent;

export type UserSessionStore = GatewayUser & SessionStore | FirebaseUser & SessionStore | HxAuthUser & SessionStore | null;

const extractToken = (hash: string) => {
  const match = hash.match(/access_token=([\w.-]+)/);
  return match?.[1];
};

// reducers inherently use complex switch statements
// noinspection OverlyComplexFunctionJS
const reduceUserSession: Reducer<UserSessionStore, UserSessionStoreEvent> = (user: UserSessionStore = null, event) => {
  switch (event.type) {
    case FIREBASE_USER_SIGNED_IN: {
      let account = user?.account ? user.account : new ApiUser();
      return {
        ...user,
        account: {
          role: event.payload.role,
          email: event.payload.email,
          name: event.payload.displayName || account?.name,
          userOrganization: account?.userOrganization,
          // Preserve a userId already resolved via GET /users/accounts/self. A firebase
          // onAuthStateChanged callback can re-fire FIREBASE_USER_SIGNED_IN after the account loads; dropping userId here left the confirm PUT with no id.
          userId: account?.userId,
          // eslint-disable-next-line no-undefined
          expireTime: undefined
        },
        firebaseUser: {
          displayName: event.payload.displayName,
          uid: event.payload.uid,
          role: event.payload.role,
          idToken: event.payload.idToken,
        },
        authType: user?.authType as typeof UserAuthType.FIREBASE || UserAuthType.FIREBASE
      };
    }
    case FIREBASE_SIGNED_IN_WITH_CUSTOM_TOKEN: {
      const decoded = decodeAvvirStorageToken(event.payload.token);
      return {
        ...user,
        account: {
          ...user?.account,
          name: decoded.claims?.name || user?.account?.name,
          email: decoded.uid || user?.account?.email,
        }
      };
    }
    case USER_SIGNED_OUT: {
      return null;
    }
    case GATEWAY_USER_SIGNED_IN: {
      let account = user?.account ? user.account : new ApiUser();
      const decoded = decodeAvvirGatewayToken(event.payload.jwt);
      // A gateway sign-in for a different email is a new identity (e.g. accepting an invite in a
      // browser that still holds a prior session). Carrying the previous user's id/name/org forward
      // makes the confirm PUT target the wrong account -> 403 (session bleed). Only preserve
      // identity fields when the same user is re-authenticating.
      const sameUser = account?.email?.toLowerCase() === decoded.sub?.toLowerCase();
      return {
        ...user,
        account: {
          role: decoded.role,
          email: decoded.sub,
          // eslint-disable-next-line no-undefined
          name: sameUser ? account?.name : undefined,
          // eslint-disable-next-line no-undefined
          userOrganization: sameUser ? account?.userOrganization : undefined,
          projectId: event.payload.projectId,
          // eslint-disable-next-line no-undefined
          userId: sameUser ? account?.userId : undefined,
          expireTime: decoded.exp
        },
        authType: UserAuthType.GATEWAY_JWT,
        gatewayUser: {
          idToken: event.payload.jwt,
          role: decoded.role
        }
      };
    }
    case HXAUTH_USER_SIGNED_IN: {
      const decoded = decodeJwtToPayload(event.payload.accessToken) as { exp: number, name?: string, email?: string, preferred_username?: string };
      return {
        ...user,
        account: {
          ...user?.account,
          name: decoded.name || user?.account?.name,
          email: decoded.email || decoded.preferred_username || user?.account?.email,
        },
        authType: UserAuthType.HXAUTH_ACCESS_TOKEN,
        hxAuthUser: {
          realmName: event.payload.realmName,
          accessToken: event.payload.accessToken,
          refreshToken: event.payload.refreshToken,
          expireTime: decoded.exp
        }
      };
    }
    case "to_procore_authenticated_page": {
      return {
        ...user,
        procoreAccessToken: extractToken(window.location.hash),
      };
    }
    case GCP_BEARER_TOKEN_LOADED: {
      return {
        ...user,
        gcpBearerTokensByFirebaseOrganizationId: {
          ...user?.gcpBearerTokensByFirebaseOrganizationId,
          [event.payload.organizationId]: {
            ...event.payload,
            expireTime: new Date(event.payload.expireTime),
          }
        }
      };
    }
    case USER_ACCOUNT_UPDATED: {
      return {
        ...user,
        account: event.payload.user
      };
    }
    case USER_ACCOUNT_LOADED: {
      const incoming = event.payload.user;
      return {
        ...user,
        account: {
          ...user?.account,
          ...incoming,
          name: incoming.name || user?.account?.name
        }
      };
    }
    case USER_PERMISSIONS_LOADED: {
      return {
        ...user,
        permissions: event.payload.permissions
      };
    }
    default:
      return user;
  }
};

export default reduceUserSession;
